import { IConnectionSummary, IJobExecutionsStatus, ISearchDeviceInfo } from '../../dto/device-dto';
import { ISnapshotProps } from '../software-snapshot-dto';
import { ComponentVersionDto } from '../raptor-component-version.dto';
import { IDeploymentTargetDeviceGroupOptions, ITargetOptions } from './target-dto';

export interface IDeploymentUnit {
    type: string;
    data: {
        commandCode: string;
        commandPayload?: any;
    };
    releaseIds: number[];
}

export interface IDeploymentTarget {
    type: string;
    scheduleDateTime: Date;
    targetNames: string[];
    deviceGroupIds: number[];
    dpuOverride?: boolean;
    downloadOnly?: boolean;
    installDateTime?: Date;
}

export interface ISaveDeploymentProps {
    groupId: number;
    label: string;
    releaseId: number;
    removalPackages: string[];
    deploymentTargets: IDeploymentTarget[];
}

export enum UnifiedDeploymentUnitType {
    RELEASE = 'Release',
    COMPONENT_VERSION = 'ComponentVersion',
}

export interface IUnifiedDeploymentUnitOptions {
    removalPackages?: string[];
}

export interface ICreateUnifiedDeploymentRequest {
    groupId: number;
    label: string;
    accountUuid?: string;
    deploymentContent: {
        unitType: UnifiedDeploymentUnitType;
        units: Array<{
            uuid: string;
            options: IUnifiedDeploymentUnitOptions;
        }>;
    };
    scheduleDateTime: string;
}

export class UnifiedDeploymentResponseDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get uuid(): string {
        return this._object['uuid'] || '';
    }

    get id(): number {
        return this._object['id'] || 0;
    }

    get label(): string {
        return this._object['label'] || '';
    }

    get targetDeviceNames(): string[] {
        return this._object['targetDeviceNames'] || [];
    }

    get targetDeviceGroupNames(): string[] {
        return this._object['targetDeviceGroupNames'] || [];
    }

    get status(): string {
        return this._object['status'] || '';
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export enum DeploymentStatus {
    CREATED = 'CREATED',
    SCHEDULED = 'SCHEDULED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
    FAILED = 'FAILED',
}

export interface ICreatedDeploymentOptions {
    uuid: string;
    id: number;
    label: string;
    targetDeviceNames: string[];
    targetDeviceGroupNames: string[];
}

export interface IDeploymentUnitDto {
    id: number;
    label: string;
    sequenceNumber: number;
    deploymentId: number;
    type: string;
    executionId: string;
    data?: {
        commandCode: string;
        commandPayload?: any;
    };
    releases?: ISnapshotProps[]; // Can't change `releases` -> `snapshots`, because this is database table
    componentVersions: ComponentVersionDto[];
}
export interface IDeploymentOptions {
    id: number;
    label: string;
    accountUuid: string;
    sequenceNumber: number;
    autoComplete: boolean;
    status: DeploymentStatus;
    createdAt: string;
    deploymentGroupId?: number;
    deploymentUnits?: IDeploymentUnitDto[];
    deploymentTargets?: ITargetOptions[];
    jobExecutionsRolloutConfig?: any;
    abortConfig?: any;
}

export enum PROGRESS_STATUS {
    PLAY = 'PLAY',
    PAUSE = 'PAUSE',
    ERROR = 'ERROR',
    SUCCESS = 'SUCCESS',
}

export enum PROGRESS_STAGE {
    QUEUE = 'QUEUE',
    IN_PROGRESS = 'IN_PROGRESS',
    DOWNLOADED = 'DOWNLOADED',
    SUCCEEDED = 'SUCCEEDED',
    FAILED = 'FAILED',
}

export interface IProgressStatusProps {
    progressStatus?: PROGRESS_STATUS;
    progressStage?: PROGRESS_STAGE;
}

export type TSearchDeviceInfoProgressDetails = ISearchDeviceInfo & {
    jobInformation?: IJobExecutionsStatus;
    progressStatus?: IProgressStatusProps;
    connectivity?: IConnectionSummary;
}

interface TDeploymentTargetProgressDetails extends ITargetOptions {
    deviceGroups: IDeploymentTargetDeviceGroupOptions[];
}

export interface IDeploymentProgressDetails extends IDeploymentOptions {
    deploymentTargets?: TDeploymentTargetProgressDetails[];
}
